import { useMemo } from 'react';
import { ContentStatus } from '@diritto/common/content/constants';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { useAuthState } from 'contexts/AuthProvider';
import { useContentState } from 'state-hooks/contents/useContentState';
import {
  BADGE_EXCLUDES,
  BadgeMode,
  BadgePosition,
  ContentBadge,
  ContentBadgeState,
  MODE_LAYOUT,
} from 'constants/badge';

export const useContentBadges = (content: ContentDto | ContentPublicRelayDto, mode: BadgeMode): ContentBadgeState => {
  const { partner } = useAuthState();
  const state = useContentState(content);

  return useMemo(() => {
    if (content.status === ContentStatus.Private) {
      return { badgesByPosition: {} };
    }

    const showPartner = !!(partner && partner.verifiedAt);

    const activeBadges = new Set<ContentBadge>();

    if (state.isNew) {
      activeBadges.add(ContentBadge.NEW);
    }
    if (state.isDirittoOriginal) {
      activeBadges.add(ContentBadge.ORIGINAL);
    }
    if (state.isDirittoOnly) {
      activeBadges.add(ContentBadge.ONLY);
    }
    if (state.isUpdated) {
      activeBadges.add(ContentBadge.UP);
    }
    if (state.isCompleted) {
      activeBadges.add(ContentBadge.COMPLETED);
    }
    if (state.isPublished) {
      activeBadges.add(ContentBadge.PUBLISHED);
    }
    if (state.isContestActive || state.isContestWinner) {
      activeBadges.add(ContentBadge.PICK_CHOOSE);
    }
    if (state.isAdult) {
      activeBadges.add(ContentBadge.ADULT);
    }
    if (state.showHiddenReservationBadge) {
      activeBadges.add(ContentBadge.HIDE_RESERVE);
    }
    if (state.isHidden) {
      activeBadges.add(ContentBadge.HIDDEN);
    }
    if (showPartner && state.isProposalWanted) {
      activeBadges.add(ContentBadge.UNCONTRACTED);
    }

    // 혼용이 불가능한 뱃지에 대한 예외처리 로직
    for (const badge of Object.values(ContentBadge)) {
      if (activeBadges.has(badge)) {
        const excluded = BADGE_EXCLUDES[badge];
        if (excluded && activeBadges.has(excluded)) {
          activeBadges.delete(excluded);
        }
      }
    }

    const layout = MODE_LAYOUT[mode];
    const badgesByPosition: Partial<Record<BadgePosition, ContentBadge[]>> = {};

    for (const [position, badges] of Object.entries(layout)) {
      const visible = (badges as ContentBadge[]).filter(b => activeBadges.has(b));
      if (visible.length > 0) {
        badgesByPosition[position as BadgePosition] = visible;
      }
    }

    return {
      badgesByPosition,
    };
  }, [content.status, state, partner, mode]);
};
